import { create } from "zustand";
import { devtools } from "zustand/middleware";
import type { Campaign } from "../types/campaign";
import { apiService } from "../services/api";

interface CampaignState {
  // Data
  campaigns: Campaign[];
  selectedCampaign: string | null;

  // Pagination metadata
  totalCount: number;
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;

  // Loading states
  loading: boolean;

  // Actions
  fetchCampaigns: (params?: Record<string, unknown>) => Promise<void>;
  selectCampaign: (campaignId: string | null) => void;
  addCampaign: (campaign: Omit<Campaign, "created_at" | "updated_at">) => Promise<void>;
  updateCampaign: (
    campaignId: string,
    updates: Partial<Pick<Campaign, "start_date" | "end_date" | "bike_ids" | "city_ids">>
  ) => Promise<void>;
  removeCampaign: (campaignId: string) => Promise<void>;
  refreshCampaigns: () => Promise<void>;
}

export const useCampaignStore = create<CampaignState>()(
  devtools(
    (set, get) => ({
      // Initial state
      campaigns: [],
      selectedCampaign: null,
      totalCount: 0,
      currentPage: 1,
      totalPages: 1,
      itemsPerPage: 20,
      loading: false,

      // Actions
      fetchCampaigns: async (params = { page: 1, limit: 20 }) => {
        set({ loading: true });
        try {
          const response = (await apiService.getCampaigns(params)) as {
            campaigns: Campaign[];
            pagination?: {
              total: number;
              page: number;
              limit: number;
              totalPages: number;
            };
          };

          // Handle pagination metadata if available
          const pagination = response.pagination || {
            total: response.campaigns.length,
            page: 1,
            limit: response.campaigns.length,
            totalPages: 1,
          };

          set({
            campaigns: response.campaigns,
            totalCount: pagination.total,
            currentPage: pagination.page,
            totalPages: pagination.totalPages,
            itemsPerPage: pagination.limit,
            loading: false,
          });
        } catch (error) {
          console.error("Error fetching campaigns:", error);
          set({ loading: false });
        }
      },

      selectCampaign: (campaignId) => set({ selectedCampaign: campaignId }),

      addCampaign: async (campaign) => {
        try {
          const newCampaign = (await apiService.createCampaign(campaign)) as Campaign;
          const { campaigns } = get();
          set({ campaigns: [...campaigns, newCampaign] });
        } catch (error) {
          console.error("Error adding campaign:", error);
          throw error;
        }
      },

      updateCampaign: async (campaignId, updates) => {
        try {
          const updatedCampaign = (await apiService.updateCampaign(
            campaignId,
            updates
          )) as Campaign;
          const { campaigns } = get();
          const updatedCampaigns = campaigns.map((campaign) =>
            campaign.campaign_id === campaignId ? { ...campaign, ...updatedCampaign } : campaign
          );
          set({ campaigns: updatedCampaigns });
        } catch (error) {
          console.error("Error updating campaign:", error);
          throw error;
        }
      },

      removeCampaign: async (campaignId) => {
        try {
          await apiService.deleteCampaign(campaignId);
          const { campaigns } = get();
          const filteredCampaigns = campaigns.filter(
            (campaign) => campaign.campaign_id !== campaignId
          );
          set({ campaigns: filteredCampaigns });
        } catch (error) {
          console.error("Error removing campaign:", error);
          throw error;
        }
      },

      refreshCampaigns: async () => {
        const { fetchCampaigns, currentPage, itemsPerPage } = get();
        await fetchCampaigns({
          page: currentPage,
          limit: itemsPerPage,
        });
      },
    }),
    { name: "campaign-store" }
  )
);
